import { theme as CoreTheme } from '@back-office/core';
import { useMediaQuery } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import React, { ChangeEvent, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import GcpBox from '../gcp-box';
import GcpTypography from '../gcp-typography';
import {
  GcpVerticalTabComponent,
  mainMenu,
  paymentRequestMenu,
  recurringRequestMenu,
  userMenu,
} from './content';
import GcpTabLabel from './TabLabel';

export enum UserRole {
  admin = 'ADMIN',
  user = 'USER',
}

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  page: GcpVerticalTabComponent;
  value: GcpVerticalTabComponent;
  isTabletOrMobile: boolean;
}

interface GcpVerticalTabsProps {
  dashboard: ReactNode;
  transactions: ReactNode;
  qr: ReactNode;
  invitation: ReactNode;
  directory: ReactNode;
  stage: GcpVerticalTabComponent;
  paymentFile: ReactNode;
  handleOnStageChange: (newStage: GcpVerticalTabComponent) => void;
  userRole: string;
  billerEnrollment: ReactNode;
  visibleBillerRequest: boolean;
  visiblePaymentRequest: boolean;
  isRecurringMerchant: boolean;
}

function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, page, index, isTabletOrMobile, ...rest } = props;

  return (
    <GcpBox
      aria-labelledby={`vertical-tab-${index}`}
      hidden={value !== page}
      id={`vertical-panel-${index}`}
      isFullWidth={!isTabletOrMobile}
      mb={3}
      ml={!isTabletOrMobile && 4}
      mt={3}
      role="tabpanel"
      {...rest}
    >
      {value === page && children}
    </GcpBox>
  );
}

function a11yProps(index: number): { 'aria-controls': string; id: string } {
  return {
    id: `vertical-tabpanel-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

export interface StyleProps {
  isTabletOrMobile: boolean;
}

const useStyles = makeStyles<Theme, StyleProps>(
  (theme: Theme) => ({
    root: {
      flexGrow: ({ isTabletOrMobile }) => !isTabletOrMobile && 1,
      backgroundColor: theme.palette.background.paper,
      display: ({ isTabletOrMobile }) => (isTabletOrMobile ? 'block' : 'flex'),
      height: '85vh',
      marginTop: ({ isTabletOrMobile }) =>
        isTabletOrMobile ? theme.spacing(1) : theme.spacing(3),
    },
    tabs: {
      border: `1px solid ${theme.palette.common.concrete}`,
      boxSizing: 'border-box',
      borderRadius: ({ isTabletOrMobile }) =>
        isTabletOrMobile ? '7px' : '20px',
      padding: ({ isTabletOrMobile }) =>
        !isTabletOrMobile && theme.spacing(5, 0),
      width: ({ isTabletOrMobile }) => !isTabletOrMobile && theme.spacing(30),
    },
    indicator: {
      left: 0,
      backgroundColor: theme.palette.common.ceriseRed,
    },
    tab: {
      flexDirection: 'row',
      padding: theme.spacing(0, 2),
      justifyContent: ({ isTabletOrMobile }) =>
        !isTabletOrMobile && 'flex-start',
    },
    wrapper: {
      flexDirection: 'row',
      padding: theme.spacing(0, 2),
      justifyContent: ({ isTabletOrMobile }) =>
        !isTabletOrMobile && 'flex-start',
      '& > p': {
        marginLeft: ({ isTabletOrMobile }) =>
          !isTabletOrMobile && theme.spacing(2),
      },
    },
    disabled: {
      opacity: '1 !important',
    },
  }),
  { name: 'GcpVerticalTabs' }
);

const GcpVerticalTabs: FC<GcpVerticalTabsProps> = (props) => {
  const isTabletOrMobile = useMediaQuery(CoreTheme.breakpoints.down('md'));
  const classes = useStyles({ isTabletOrMobile });
  const { t } = useTranslation();
  const {
    dashboard,
    qr,
    directory,
    invitation,
    transactions,
    paymentFile,
    billerEnrollment,
    stage: value,
    handleOnStageChange,
    userRole,
    isRecurringMerchant,
    visibleBillerRequest,
    visiblePaymentRequest,
  } = props;

  const handleChange = (
    event: ChangeEvent<unknown>,
    newValue: GcpVerticalTabComponent
  ): void => {
    handleOnStageChange(newValue);
  };

  return (
    <div className={classes.root}>
      <Tabs
        aria-label="GCP Vertical Tab"
        classes={{ root: classes.tabs, indicator: classes.indicator }}
        orientation={isTabletOrMobile ? 'horizontal' : 'vertical'}
        scrollButtons="desktop"
        value={value}
        variant="scrollable"
        onChange={handleChange}
      >
        {!isTabletOrMobile && (
          <Tab
            classes={{
              wrapper: classes.tab,
              disabled: classes.disabled,
            }}
            label={
              <GcpTypography
                paint="ceriseRed"
                transform="uppercase"
                variant="overline"
              >
                {t('appbar.menu')}
              </GcpTypography>
            }
            {...a11yProps(0)}
            disabled
          />
        )}
        {mainMenu.map((menu) => (
          <Tab
            key={menu.translation}
            classes={{ wrapper: classes.wrapper }}
            label={
              <GcpTabLabel
                Icon={menu.icon}
                stage={menu.stage}
                translation={menu.translation}
                value={value}
              />
            }
            {...a11yProps(menu.id)}
            value={menu.stage}
          />
        ))}
        {isRecurringMerchant &&
          (userRole === UserRole.admin ||
            (userRole === UserRole.user && visibleBillerRequest)) &&
          recurringRequestMenu.map((menu) => (
            <Tab
              key={menu.translation}
              classes={{ wrapper: classes.wrapper }}
              label={
                <GcpTabLabel
                  Icon={menu.icon}
                  stage={menu.stage}
                  translation={menu.translation}
                  value={value}
                />
              }
              {...a11yProps(menu.id)}
              value={menu.stage}
            />
          ))}
        {isRecurringMerchant &&
          (userRole === UserRole.admin ||
            (userRole === UserRole.user && visiblePaymentRequest)) &&
          paymentRequestMenu.map((menu) => (
            <Tab
              key={menu.translation}
              classes={{ wrapper: classes.wrapper }}
              label={
                <GcpTabLabel
                  Icon={menu.icon}
                  stage={menu.stage}
                  translation={menu.translation}
                  value={value}
                />
              }
              {...a11yProps(menu.id)}
              value={menu.stage}
            />
          ))}
        {!isTabletOrMobile && userRole === UserRole.admin && (
          <Tab
            classes={{
              wrapper: classes.tab,
              disabled: classes.disabled,
            }}
            label={
              <GcpTypography
                paint="ceriseRed"
                transform="uppercase"
                variant="overline"
              >
                {t('appbar.user')}
              </GcpTypography>
            }
            {...a11yProps(4)}
            disabled
          />
        )}
        {userRole === UserRole.admin &&
          userMenu.map((menu) => (
            <Tab
              key={menu.translation}
              classes={{ wrapper: classes.wrapper }}
              label={
                <GcpTabLabel
                  Icon={menu.icon}
                  stage={menu.stage}
                  translation={menu.translation}
                  value={value}
                />
              }
              {...a11yProps(menu.id)}
              value={menu.stage}
            />
          ))}
      </Tabs>
      <TabPanel
        index={1}
        isTabletOrMobile={isTabletOrMobile}
        page={GcpVerticalTabComponent.Dashboard}
        value={value}
      >
        {dashboard}
      </TabPanel>
      <TabPanel
        index={2}
        isTabletOrMobile={isTabletOrMobile}
        page={GcpVerticalTabComponent.Transactions}
        value={value}
      >
        {transactions}
      </TabPanel>
      <TabPanel
        index={2}
        isTabletOrMobile={isTabletOrMobile}
        page={GcpVerticalTabComponent.BillerEnrollment}
        value={value}
      >
        {billerEnrollment}
      </TabPanel>
      <TabPanel
        index={2}
        isTabletOrMobile={isTabletOrMobile}
        page={GcpVerticalTabComponent.PaymentFile}
        value={value}
      >
        {paymentFile}
      </TabPanel>
      <TabPanel
        index={3}
        isTabletOrMobile={isTabletOrMobile}
        page={GcpVerticalTabComponent.CreateQR}
        value={value}
      >
        {qr}
      </TabPanel>
      <TabPanel
        index={5}
        isTabletOrMobile={isTabletOrMobile}
        page={GcpVerticalTabComponent.Invitation}
        value={value}
      >
        {invitation}
      </TabPanel>
      <TabPanel
        index={6}
        isTabletOrMobile={isTabletOrMobile}
        page={GcpVerticalTabComponent.Directory}
        value={value}
      >
        {directory}
      </TabPanel>
    </div>
  );
};

export default GcpVerticalTabs;
